import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Plus, Pencil } from "lucide-react";
import { Employee } from "../types/employee";
import api from "../services/api";
import ShiftModal from "./ShiftModal";
import EmployeeRegularShiftsModal from "./EmployeeRegularShiftsModal";

const DAY_NAME_TO_INDEX: Record<string, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

const REGULAR_SHIFT_DAY_CONFIG = [
  { key: "monday", label: "Monday", dayIndex: 1 },
  { key: "tuesday", label: "Tuesday", dayIndex: 2 },
  { key: "wednesday", label: "Wednesday", dayIndex: 3 },
  { key: "thursday", label: "Thursday", dayIndex: 4 },
  { key: "friday", label: "Friday", dayIndex: 5 },
  { key: "saturday", label: "Saturday", dayIndex: 6 },
  { key: "sunday", label: "Sunday", dayIndex: 0 },
] as const;

interface Shift {
  id?: number;
  employee_id: number;
  date: string;
  start_time?: string;
  end_time?: string;
  break_duration?: number;
  isOverride?: boolean; // Flag to identify override shifts
}

interface RegularSchedule {
  id: number;
  employee_id: number;
  days: string;
  is_working: string;
  start_time?: string | null;
  end_time?: string | null;
  location_start_time?: string | null;
  location_end_time?: string | null;
}

interface ScheduleOverride {
  id: number;
  employee_id: number;
  date: string;
  start_time?: string | null;
  end_time?: string | null;
  is_working?: string;
}

type EmployeeWithShifts = Partial<Employee> & {
  id: number;
  name: string;
  email?: string | null;
  mobile?: string | null;
  user_image_url?: string | null;
  regular_schedules?: RegularSchedule[];
  schedule_overrides?: ScheduleOverride[];
};

interface RegularShiftModalEntry {
  day: string;
  dayIndex: number;
  isWorking: boolean;
  startTime: string;
  endTime: string;
  scheduleId?: number;
}

interface ScheduledShiftsProps {
  employees?: Employee[];
}

const ScheduledShifts: React.FC<ScheduledShiftsProps> = ({ employees }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [teamMembers, setTeamMembers] = useState<EmployeeWithShifts[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithShifts | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isRegularShiftsModalOpen, setIsRegularShiftsModalOpen] = useState(false);
  const [selectedEmployeeForRegularShifts, setSelectedEmployeeForRegularShifts] = useState<EmployeeWithShifts | null>(null);
  const [regularShiftsForModal, setRegularShiftsForModal] = useState<RegularShiftModalEntry[] | null>(null);

  const loadEmployees = useCallback(async () => {
    let shouldFetchFromApi = true;

    if (employees && employees.length > 0) {
      const enrichedEmployees: EmployeeWithShifts[] = employees.map((emp) => ({
        ...emp,
        regular_schedules: (emp as unknown as EmployeeWithShifts).regular_schedules ?? [],
        schedule_overrides: (emp as unknown as EmployeeWithShifts).schedule_overrides ?? [],
      }));

      setTeamMembers(enrichedEmployees);

      const hasScheduleData = enrichedEmployees.some(
        (emp) =>
          (emp.regular_schedules && emp.regular_schedules.length > 0) ||
          (emp.schedule_overrides && emp.schedule_overrides.length > 0)
      );

      if (hasScheduleData) {
        shouldFetchFromApi = false;
      }
    }

    if (!shouldFetchFromApi) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.get("/admin/employees-shifts");
      const employeesData: EmployeeWithShifts[] = response.data?.employees ?? [];
      setTeamMembers(employeesData);
    } catch (error) {
      console.error("Error fetching employee shifts:", error);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  }, [employees]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Get the start of the week (Monday)
  const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // Get all days of the current week
  const getWeekDays = (): Date[] => {
    const weekStart = getWeekStart(currentWeek);
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Format date for display
  const formatDate = (date: Date): string => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Format date for API
  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const normalizeTime = (value?: string | null): string => {
    if (!value) return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    const timePart = trimmed.includes("T") ? trimmed.split("T")[1] ?? "" : trimmed;
    const clean = timePart.replace("Z", "");
    return clean.slice(0, 5);
  };

  const formatTimeDisplay = (time?: string): string => {
    if (!time) return "";
    const sanitized = time.length > 5 ? time.slice(0, 5) : time;
    const [hourStr, minuteStr = "00"] = sanitized.split(":");
    const hour = parseInt(hourStr, 10);
    if (Number.isNaN(hour)) return "";

    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12.toString().padStart(2, "0")}:${minuteStr} ${period}`;
  };

  const getShiftLabel = (shift: Shift): string => {
    const start = formatTimeDisplay(shift.start_time);
    const end = formatTimeDisplay(shift.end_time);

    if (!start && !end) {
      return "-";
    }

    if (!start || !end) {
      return start || end || "-";
    }

    return `${start} - ${end}`;
  };

  const formatTimeForBackend = (time?: string): string | null => {
    if (!time) return null;

    const [hourStr, minuteStr = "00"] = time.split(":");
    const hour24 = parseInt(hourStr, 10);

    if (Number.isNaN(hour24)) {
      return null;
    }

    const period = hour24 >= 12 ? "pm" : "am";
    let hour12 = hour24 % 12;
    if (hour12 === 0) {
      hour12 = 12;
    }

    const hourFormatted = hour12.toString().padStart(2, "0");
    return `${hourFormatted}:${minuteStr} ${period}`;
  };

  const buildWeekShifts = useCallback(
    (members: EmployeeWithShifts[], weekDate: Date): Shift[] => {
      if (!members.length) {
        return [];
      }

      const weekStart = getWeekStart(weekDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const computedShifts: Shift[] = [];

      members.forEach((member) => {
        if (!member?.id) {
          return;
        }

        const memberId = member.id;
        const baseSchedules = member.regular_schedules ?? [];

        baseSchedules.forEach((schedule) => {
          if (!schedule) {
            return;
          }

          const isWorking =
            !schedule.is_working ||
            schedule.is_working.toLowerCase() === "yes";
          if (!isWorking) {
            return;
          }

          const dayKey = (schedule.days || "").toLowerCase();
          const dayIndex = DAY_NAME_TO_INDEX[dayKey];

          if (dayIndex === undefined) {
            return;
          }

          const scheduleDate = new Date(weekStart);
          scheduleDate.setDate(weekStart.getDate() + dayIndex);

          const startTime =
            normalizeTime(schedule.start_time) ||
            normalizeTime(schedule.location_start_time);
          const endTime =
            normalizeTime(schedule.end_time) ||
            normalizeTime(schedule.location_end_time);

          if (!startTime || !endTime) {
            return;
          }

          computedShifts.push({
            id: schedule.id,
            employee_id: memberId,
            date: formatDateForAPI(scheduleDate),
            start_time: startTime,
            end_time: endTime,
          });
        });

        const overrides = member.schedule_overrides ?? [];
        overrides.forEach((override) => {
          if (!override?.date) {
            return;
          }

          const overrideDate = new Date(override.date);
          const startBoundary = new Date(weekStart);
          startBoundary.setHours(0, 0, 0, 0);
          const endBoundary = new Date(weekEnd);
          endBoundary.setHours(23, 59, 59, 999);

          if (
            overrideDate < startBoundary ||
            overrideDate > endBoundary
          ) {
            return;
          }

          const overrideDateStr = formatDateForAPI(overrideDate);

          for (let i = computedShifts.length - 1; i >= 0; i--) {
            if (
              computedShifts[i].employee_id === memberId &&
              computedShifts[i].date === overrideDateStr
            ) {
              computedShifts.splice(i, 1);
            }
          }

          if (
            override.is_working &&
            override.is_working.toLowerCase() !== "yes"
          ) {
            return;
          }

          const startTime = normalizeTime(override.start_time);
          const endTime = normalizeTime(override.end_time);

          if (!startTime || !endTime) {
            return;
          }

          computedShifts.push({
            id: override.id,
            employee_id: memberId,
            date: overrideDateStr,
            start_time: startTime,
            end_time: endTime,
            isOverride: true, // Mark as override shift
          });
        });
      });

      return computedShifts;
    },
    []
  );

  const createDefaultRegularShiftEntries = (): RegularShiftModalEntry[] =>
    REGULAR_SHIFT_DAY_CONFIG.map((day) => ({
      day: day.label,
      dayIndex: day.dayIndex,
      isWorking: false,
      startTime: "",
      endTime: "",
      scheduleId: undefined,
    }));

  const mapSchedulesToRegularShiftEntries = (
    schedules: RegularSchedule[]
  ): RegularShiftModalEntry[] => {
    const entries = createDefaultRegularShiftEntries();

    schedules.forEach((schedule) => {
      if (!schedule) {
        return;
      }

      const dayKey = (schedule.days || "").toLowerCase();
      const config = REGULAR_SHIFT_DAY_CONFIG.find(
        (day) => day.key === dayKey
      );

      if (!config) {
        return;
      }

      const entry = entries.find(
        (item) => item.dayIndex === config.dayIndex
      );

      if (!entry) {
        return;
      }

      const isWorking =
        !schedule.is_working ||
        schedule.is_working.toLowerCase() === "yes";

      entry.scheduleId = schedule.id;
      entry.isWorking = isWorking;

      const startTime =
        normalizeTime(schedule.start_time) ||
        normalizeTime(schedule.location_start_time);
      const endTime =
        normalizeTime(schedule.end_time) ||
        normalizeTime(schedule.location_end_time);

      if (isWorking) {
        entry.startTime = startTime || entry.startTime;
        entry.endTime = endTime || entry.endTime;
      } else {
        entry.startTime = "";
        entry.endTime = "";
      }
    });

    return entries;
  };

  useEffect(() => {
    if (teamMembers.length === 0) {
      setShifts([]);
      return;
    }

    setShifts(buildWeekShifts(teamMembers, currentWeek));
  }, [teamMembers, currentWeek, buildWeekShifts]);

  // Get date range string
  const getDateRange = (): string => {
    const days = getWeekDays();
    const start = days[0];
    const end = days[6];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${start.getDate()} ${months[start.getMonth()]} - ${end.getDate()} ${months[end.getMonth()]}, ${end.getFullYear()}`;
  };

  // Navigate to previous week
  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  // Navigate to next week
  const goToNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  // Navigate to current week
  const goToCurrentWeek = () => {
    setCurrentWeek(new Date());
  };

  // Check if current week is the current week
  const isCurrentWeek = (): boolean => {
    const today = new Date();
    const currentWeekStart = getWeekStart(today);
    const selectedWeekStart = getWeekStart(currentWeek);
    return currentWeekStart.getTime() === selectedWeekStart.getTime();
  };

  // Get shifts for a specific employee and date
  const getShiftsForEmployeeAndDate = (
    employeeId: number,
    date: Date
  ): Shift[] => {
    const dateStr = formatDateForAPI(date);
    return shifts.filter(
      (shift) =>
        shift.employee_id === employeeId &&
        shift.date === dateStr
    );
  };

  // Handle add shift
  const handleAddShift = (employeeId: number, date: Date) => {
    const employee = teamMembers.find((emp) => emp.id === employeeId);
    if (employee) {
      setSelectedEmployee(employee);
      setSelectedDate(date);
      setIsModalOpen(true);
    }
  };

  // Handle edit shift
  const handleEditShift = (shift: Shift) => {
    const employee = teamMembers.find((emp) => emp.id === shift.employee_id);
    if (employee) {
      setSelectedEmployee(employee);
      const shiftDate = new Date(shift.date);
      setSelectedDate(shiftDate);
      setIsModalOpen(true);
    }
  };

  // Handle save shifts from modal
  const handleSaveShifts = async (timeSlots: { start_time: string; end_time: string; id?: string }[]) => {
    if (!selectedEmployee || !selectedDate) return;

    setSaving(true);
    try {
      const dateStr = formatDateForAPI(selectedDate);

      // Format time to HH:MM:SS for backend
      const formatTimeForBackend = (time: string): string => {
        if (!time || time.length < 5) return "";
        // If already in HH:MM:SS format, return as is
        if (time.length >= 8) return time;
        // Convert HH:MM to HH:MM:SS
        return `${time}:00`;
      };

      // Transform timeSlots to arrays of start_time and end_time as backend expects
      const startTimes: string[] = [];
      const endTimes: string[] = [];

      timeSlots.forEach((slot) => {
        const startTime = formatTimeForBackend(slot.start_time);
        const endTime = formatTimeForBackend(slot.end_time);
        if (startTime && endTime) {
          startTimes.push(startTime);
          endTimes.push(endTime);
        }
      });

      if (startTimes.length === 0 || endTimes.length === 0) {
        alert("Please add at least one shift with start and end times.");
        setSaving(false);
        return;
      }

      let shouldReloadFromApi = false;

      try {
        const response = await api.post("/admin/employee-schedule-shift-overide", {
          employee_id: selectedEmployee.id,
          date: dateStr,
          start_time: startTimes,
          end_time: endTimes,
        });

        if (response.data?.status === "success" || response.data?.success) {
          shouldReloadFromApi = true;
        } else {
          throw new Error("Shift save response did not indicate success.");
        }
      } catch (apiError) {
        console.warn("Error saving shifts via API, applying local fallback:", apiError);
        const newShifts: Shift[] = timeSlots.map((slot, index) => ({
          id: Date.now() + index,
          employee_id: selectedEmployee.id,
          date: dateStr,
          start_time: slot.start_time,
          end_time: slot.end_time,
        }));

        setShifts((prevShifts) => [
          ...prevShifts.filter(
            (s) => !(s.employee_id === selectedEmployee.id && s.date === dateStr)
          ),
          ...newShifts,
        ]);
      }

      if (shouldReloadFromApi) {
        await loadEmployees();
      }

      setIsModalOpen(false);
      setSelectedEmployee(null);
      setSelectedDate(null);
    } catch (error) {
      console.error("Error saving shifts:", error);
      alert("Failed to save shifts. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
    setSelectedDate(null);
  };

  // Get existing shifts for modal
  const getExistingShiftsForModal = (): { id: string; start_time: string; end_time: string }[] => {
    if (!selectedEmployee || !selectedDate) return [];

    const dateStr = formatDateForAPI(selectedDate);
    return shifts
      .filter(
        (shift) =>
          shift.employee_id === selectedEmployee.id && shift.date === dateStr
      )
      .map((shift) => ({
        id: shift.id?.toString() || Date.now().toString(),
        start_time: shift.start_time || "",
        end_time: shift.end_time || "",
      }));
  };

  // Handle edit employee (opens regular shifts modal)
  const handleEditEmployee = async (employee: EmployeeWithShifts) => {
    setSelectedEmployeeForRegularShifts(employee);

    if (employee.regular_schedules && employee.regular_schedules.length > 0) {
      setRegularShiftsForModal(
        mapSchedulesToRegularShiftEntries(employee.regular_schedules)
      );
    } else {
      setRegularShiftsForModal(createDefaultRegularShiftEntries());
    }

    setIsRegularShiftsModalOpen(true);

    setSaving(true);
    try {
      const response = await api.get(`/admin/employee-schedule/${employee.id}`);
      const schedules: RegularSchedule[] = response.data?.schedules ?? [];
      if (schedules.length > 0) {
        setRegularShiftsForModal(
          mapSchedulesToRegularShiftEntries(schedules)
        );
      }
    } catch (error) {
      console.error("Error fetching employee regular schedules:", error);
    } finally {
      setSaving(false);
    }
  };

  // Handle save regular shifts
  const handleSaveRegularShifts = async (updatedShifts: RegularShiftModalEntry[]) => {
    if (!selectedEmployeeForRegularShifts) return;

    setSaving(true);
    try {
      const updates = updatedShifts.filter(
        (shift) => shift.scheduleId && shift.isWorking
      );
      const missingIdEntries = updatedShifts.filter((shift) => !shift.scheduleId);

      if (missingIdEntries.length > 0) {
        console.warn(
          "Some schedule entries are missing scheduleId and were skipped:",
          missingIdEntries
        );
      }

      if (updates.length === 0) {
        setSaving(false);
        alert("No existing schedules found to update.");
        return;
      }

      const updateResponses = await Promise.all(
        updates.map((shift) => {
          const backendStart = formatTimeForBackend(shift.startTime);
          const backendEnd = formatTimeForBackend(shift.endTime);

          if (!backendStart || !backendEnd) {
            console.warn(
              `Skipping schedule ${shift.scheduleId} due to invalid time format.`,
              shift
            );
            return Promise.resolve({ success: false });
          }

          return api
            .post(`/admin/employee-schedule-update/${shift.scheduleId}`, {
              empid: selectedEmployeeForRegularShifts.id,
              updateStartTime: backendStart,
              updateEndTime: backendEnd,
            })
            .then((response) => ({ success: true, response }))
            .catch((error) => {
              console.error(
                `Failed to update schedule ${shift.scheduleId}:`,
                error
              );
              return { success: false, error };
            });
        })
      );

      setIsRegularShiftsModalOpen(false);
      setSelectedEmployeeForRegularShifts(null);
      setRegularShiftsForModal(null);
      await loadEmployees();
    } catch (error) {
      console.error("Error saving regular shifts:", error);
      alert("Failed to save regular shifts. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle close regular shifts modal
  const handleCloseRegularShiftsModal = () => {
    setIsRegularShiftsModalOpen(false);
    setSelectedEmployeeForRegularShifts(null);
    setRegularShiftsForModal(null);
  };

  const weekDays = getWeekDays();
  // Purple theme colors matching the application design
  const primaryPurple = "#885ABB";
  const darkPurple = "#402B69";
  const lightPurple = "#C4A6E1";
  const veryLightPurple = "#F1ECFB";
  const lightPurpleBg = "#F8F5FF";

  return (
    <div className="w-full bg-white rounded-lg shadow-sm">
      {/* Navigation Bar */}
      <div
        className="flex flex-col sm:flex-row items-center justify-between px-4 py-4 lg:px-6 lg:py-6 border-b border-gray-200 gap-4"
        style={{ backgroundColor: veryLightPurple }}
      >
        <div className="flex-1 lg:block hidden"></div>
        <div className="flex flex-wrap items-center justify-center gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousWeek}
              className="p-2 rounded-lg bg-white shadow-sm border border-transparent hover:border-purple-200 transition-colors"
              aria-label="Previous week"
            >
              <ChevronLeft className="w-5 h-5" style={{ color: primaryPurple }} />
            </button>

            <button
              onClick={goToCurrentWeek}
              className="px-4 py-2 rounded-lg text-white font-bold transition-colors hover:opacity-90 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: primaryPurple }}
            >
              This Week
            </button>

            <button
              onClick={goToNextWeek}
              className="p-2 rounded-lg bg-white shadow-sm border border-transparent hover:border-purple-200 transition-colors"
              aria-label="Next week"
            >
              <ChevronRight className="w-5 h-5" style={{ color: primaryPurple }} />
            </button>
          </div>

          <div
            className="px-4 py-2 rounded-lg border bg-white font-bold text-sm lg:text-base shadow-sm min-w-[180px] text-center"
            style={{ borderColor: lightPurple, color: darkPurple }}
          >
            {getDateRange()}
          </div>
        </div>
      </div>

      {/* Mobile Scroll Hint */}
      <div className="lg:hidden px-4 py-2 bg-purple-50/50 border-b border-purple-100 flex items-center justify-center gap-2">
        <div className="flex gap-1 animate-pulse">
          <ChevronLeft className="w-4 h-4 text-[#885ABB]" />
          <ChevronRight className="w-4 h-4 text-[#885ABB]" />
        </div>
        <span className="text-xs font-semibold text-[#885ABB] uppercase tracking-wider">
          Scroll horizontally to view week
        </span>
      </div>

      {/* Schedule Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[1000px] lg:min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="sticky left-0 z-10 px-4 py-4 text-left text-sm font-bold bg-[#F8F5FF] shadow-[2px_0_5px_rgba(0,0,0,0.05)] lg:shadow-none" style={{ color: darkPurple }}>
                Team Member
              </th>
              {weekDays.map((day, index) => (
                <th
                  key={index}
                  className="px-4 py-4 text-center text-sm font-bold bg-[#F8F5FF] border-l border-gray-200"
                  style={{ color: darkPurple }}
                >
                  {formatDate(day)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teamMembers.map((employee) => (
              <tr
                key={employee.id}
                className="border-b border-gray-200 hover:bg-purple-50/30 transition-colors"
              >
                <td className="sticky left-0 z-10 px-4 py-4 bg-white/95 backdrop-blur-sm shadow-[2px_0_5px_rgba(0,0,0,0.05)] lg:shadow-none">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold lg:font-medium" style={{ color: darkPurple }}>
                      {employee.name}
                    </span>
                    <button
                      onClick={() => handleEditEmployee(employee)}
                      className="p-1 rounded-lg hover:bg-purple-100 transition-colors"
                      aria-label={`Edit ${employee.name}`}
                    >
                      <Pencil className="w-4 h-4" style={{ color: primaryPurple }} />
                    </button>
                  </div>
                </td>
                {weekDays.map((day, dayIndex) => {
                  const dayShifts = getShiftsForEmployeeAndDate(
                    employee.id,
                    day
                  );
                  return (
                    <td
                      key={dayIndex}
                      className="px-2 py-4 border-l border-gray-200 align-top"
                    >
                      <div className="flex flex-col gap-2 min-h-[80px] py-1">
                        <div className="flex flex-col gap-2">
                          {dayShifts.length > 0 ? (
                            dayShifts.map((shift, shiftIndex) => {
                              const isOverride = shift.isOverride === true;
                              return (
                                <button
                                  type="button"
                                  key={shift.id || shiftIndex}
                                  onClick={() => handleEditShift(shift)}
                                  className="rounded-md px-3 py-2 min-h-[56px] flex items-center justify-center text-center text-sm font-medium transition-colors shadow-sm border hover:opacity-90"
                                  style={{
                                    backgroundColor: isOverride ? primaryPurple : lightPurpleBg,
                                    borderColor: isOverride ? primaryPurple : lightPurple,
                                    color: isOverride ? "#FFFFFF" : darkPurple,
                                  }}
                                  title={isOverride ? "Edit override shift" : "Edit shift"}
                                >
                                  {getShiftLabel(shift)}
                                </button>
                              );
                            })
                          ) : (
                            <div
                              className="rounded-md px-3 py-2 min-h-[56px] flex items-center justify-center text-center text-sm font-medium opacity-60 border"
                              style={{
                                backgroundColor: lightPurpleBg,
                                borderColor: lightPurple,
                                color: darkPurple,
                              }}
                            >
                              -
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => handleAddShift(employee.id, day)}
                          className="rounded-md p-2 flex items-center justify-center hover:opacity-90 transition-opacity shadow-sm border"
                          style={{ backgroundColor: lightPurpleBg, borderColor: lightPurple }}
                          aria-label={`Add shift for ${employee.name} on ${formatDate(day)}`}
                        >
                          <Plus className="w-4 h-4" style={{ color: primaryPurple }} strokeWidth={3} />
                        </button>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(loading || saving) && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: primaryPurple }}></div>
        </div>
      )}

      {teamMembers.length === 0 && !loading && !saving && (
        <div className="flex items-center justify-center py-12" style={{ color: darkPurple }}>
          <p>No team members found. Add employees to see their scheduled shifts.</p>
        </div>
      )}

      {/* Shift Modal */}
      {selectedEmployee && selectedDate && (
        <ShiftModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveShifts}
          employeeName={selectedEmployee.name}
          date={selectedDate}
          existingShifts={getExistingShiftsForModal()}
        />
      )}

      {/* Employee Regular Shifts Modal */}
      {selectedEmployeeForRegularShifts && (
        <EmployeeRegularShiftsModal
          isOpen={isRegularShiftsModalOpen}
          onClose={handleCloseRegularShiftsModal}
          onSave={handleSaveRegularShifts}
          employeeName={selectedEmployeeForRegularShifts.name}
          existingShifts={regularShiftsForModal ?? undefined}
        />
      )}
    </div>
  );
};

export default ScheduledShifts;
